import React, { useEffect, useRef, useState } from 'react';
import { Alignment, Fit, Layout, RuntimeLoader, decodeImage, useRive } from '@rive-app/react-canvas';
import AddyBaseSway from '../../media/Addy-Base-Sway.gif';
import { CELEBRATIONS, DEFAULT_MOOD, HAT_FIT_PROPS, MOODS, RIG_IMAGE_ASSETS, RIG_STATE_MACHINE, RIG_URL, SKIN_HAND_COLOR_PROP, SLOT_ENUM, assetPicturesFor, celebrationFor, cosmeticImageUrl, hatFitFor, rigValuesFor, skinHandColorFor } from './cosmetics';
import './sparks.css';

// Self-hosted runtime (scripts/copy-rive-wasm.js) so nothing is fetched from a third-party CDN.
RuntimeLoader.setWasmUrl('/addy/rive.wasm');
RuntimeLoader.setWasmFallbackUrl('/addy/rive_fallback.wasm');

/**
 * Addy played from `addy.riv` (rig v5, docs/addy-rig-contract.md). Each picture slot is a
 * referenced image placeholder (the hands have a second one for the shield pose): this component
 * hands the rig the PNG of whatever picture item is equipped in that slot and flips the slot's enum
 * to show or hide it, in the item's pose. GIF-era items (emoji
 * `riveAssetId`) have no picture and leave the slot hidden. The base GIF shows until the rig has loaded.
 *
 * The 5538x4000 artboard has wide empty margins (Addy spans roughly the middle third), so the box
 * is square with a cover fit: the middle 72% of the artboard width is shown. A wider box (`width` > `size`)
 * shows more of the sides, where held items reach; at `width` = 1.385 × `size` the whole artboard shows.
 * `bleed` keeps the box (and Addy's size and place in it) but draws the whole artboard width centred over
 * it, so held items swaying past the square are not cut off. The overhang ignores the pointer.
 *
 * `equipped`: { slot: itemKey }, `catalogue`: [{ key, slot, riveAssetId }].
 * Exactly one move boolean is on at a time: while `celebrating` it is the equipped `celebration`
 * item's clip, otherwise `mood` (sway, talking, asking; the lesson page drives it from its audio
 * state). `animate=false` renders a still (leaderboard rows): every move is off, the render loop
 * is stopped once the enums and pictures have been applied, and nudged for a few frames whenever
 * one changes.
 */
export default function AddyAvatar({ equipped = {}, catalogue = [], size = 160, width = size, className = '', animate = true, mood = DEFAULT_MOOD, celebrating = false, bleed = false }) {
  const assets = useRef({}); // referenced image asset name -> runtime asset handle
  const applied = useRef({}); // asset name -> url of the picture handed to it (latest wins)
  const pauseTimer = useRef(null);
  const [assetsSeen, setAssetsSeen] = useState(0);
  const { rive, RiveComponent } = useRive({
    src: RIG_URL,
    stateMachines: RIG_STATE_MACHINE,
    autoplay: true,
    autoBind: true,
    layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
    assetLoader: (asset) => {
      if (!asset.isImage || !RIG_IMAGE_ASSETS[asset.name]) return false;
      assets.current[asset.name] = asset;
      // A fresh handle holds no picture (the rig reloads under StrictMode's double mount), so
      // whatever was handed to the previous handle must be fetched again.
      applied.current[asset.name] = null;
      setAssetsSeen((n) => n + 1);
      return true; // supplied below, from the catalogue
    },
  });

  useEffect(() => {
    const vmi = rive?.viewModelInstance;
    if (!vmi) return undefined;
    // For a still, let the state machine run a few frames after any change, then stop it.
    const nudge = () => {
      if (animate) return;
      rive.play();
      clearTimeout(pauseTimer.current);
      pauseTimer.current = setTimeout(() => rive.pause(), 120);
    };
    const keys = assetPicturesFor(equipped, catalogue);
    for (const [name, { slot, suffix }] of Object.entries(RIG_IMAGE_ASSETS)) {
      const asset = assets.current[name];
      const key = keys[name];
      if (!asset || !key) continue;
      // The item's own folder: a placeholder shared by both hands holds either hand's picture.
      const itemSlot = catalogue.find((i) => i.key === key)?.slot ?? slot;
      const url = cosmeticImageUrl(itemSlot, key, suffix);
      if (applied.current[name] === url) continue;
      applied.current[name] = url;
      fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${r.status} ${url}`))))
        .then((buf) => decodeImage(new Uint8Array(buf)))
        .then((img) => {
          if (applied.current[name] === url) {
            asset.setRenderImage(img);
            nudge();
          }
          img.unref();
        })
        .catch(() => {
          // Missing or broken picture: hide the slot rather than leave the previous item showing.
          if (applied.current[name] !== url) return;
          applied.current[name] = null;
          const e = vmi.enum(SLOT_ENUM[slot]);
          if (e) e.value = 'none';
        });
    }
    const known = {};
    for (const prop of Object.values(SLOT_ENUM)) known[prop] = vmi.enum(prop)?.values || [];
    for (const [prop, value] of Object.entries(rigValuesFor(equipped, catalogue, known))) {
      const e = vmi.enum(prop);
      if (e && e.value !== value) e.value = value;
    }
    const handColor = vmi.color(SKIN_HAND_COLOR_PROP);
    if (handColor) handColor.value = skinHandColorFor(equipped);
    const fit = hatFitFor(equipped);
    for (const [key, prop] of Object.entries(HAT_FIT_PROPS)) {
      const n = vmi.number(prop);
      if (n) n.value = fit[key];
    }
    const move = celebrating ? celebrationFor(equipped, catalogue) : MOODS.includes(mood) ? mood : DEFAULT_MOOD;
    for (const name of [...MOODS, ...CELEBRATIONS]) {
      const b = vmi.boolean(name);
      if (b) b.value = animate && name === move;
    }
    nudge();
    return () => clearTimeout(pauseTimer.current);
  }, [rive, equipped, catalogue, animate, mood, celebrating, assetsSeen]);

  return (
    <div className={`addy-avatar ${className}`} style={{ width, height: size }} aria-label="Addy">
      <RiveComponent className={bleed ? 'addy-avatar__bleed' : undefined} style={bleed ? undefined : { width: '100%', height: '100%' }} />
      {!rive && <img className="addy-avatar__placeholder" src={AddyBaseSway} alt="Addy" />}
    </div>
  );
}
